import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { SectionCard } from "@/components/common";
import type { Activity } from "@/types";
import { AdminActivityActiveSection } from "./AdminActivityActiveSection";
import { AdminActivityCommissionSection } from "./AdminActivityCommissionSection";
import { AdminActivityCategorySection } from "./AdminActivityCategorySection";
import { AdminActivitySalesAgentSection } from "./AdminActivitySalesAgentSection";
import { AdminActivityPromoSection } from "./AdminActivityPromoSection";

import type { ActivityStaffPanelAccess } from "../activityStaffAccess";

interface ActivityStaffPanelProps {
  activity: Activity;
  access: ActivityStaffPanelAccess;
}

/**
 * Content of the staff-only "Staff" tab on the activity detail page:
 * platform settings (active switch, commission, type, sales agent) and the
 * promotional discount editor. Each block saves independently.
 */
export function ActivityStaffPanel({
  activity,
  access,
}: ActivityStaffPanelProps) {
  const { t } = useTranslation("activities");
  const showSettings =
    access.active || access.salesAgent || access.commission || access.category;

  return (
    <div className="space-y-6">
      {showSettings ? (
        <SectionCard
          title={t("detail.staffPanelTitle")}
          icon={ShieldCheck}
          className="animate-slide-up"
          contentClassName="divide-y divide-surface-100 space-y-0"
        >
          {access.active ? (
            <AdminActivityActiveSection activity={activity} />
          ) : null}
          {access.commission ? (
            <AdminActivityCommissionSection activity={activity} />
          ) : null}
          {access.category ? (
            <AdminActivityCategorySection activity={activity} />
          ) : null}
          {access.salesAgent ? (
            <AdminActivitySalesAgentSection activityId={activity.id} />
          ) : null}
        </SectionCard>
      ) : null}

      {access.promo ? (
        <AdminActivityPromoSection
          key={activity.id}
          activity={activity}
        />
      ) : null}
    </div>
  );
}
